import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ApiResponse,
  PaginatedPayload,
  PaginationMeta,
} from "types/ApiResponse";
import { CategoryData } from "types/category.api";
import { deleteCategory, getCategories, manageCategory } from "./thunk";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

interface CategoryStatus {
  fetch: RequestStatus;
  manange: RequestStatus;
  delete: RequestStatus;
}

interface CategoryErrors {
  fetch: string | null;
  manange: string | null;
  delete: string | null;
}

export interface CategoryState {
  categories: CategoryData[];
  ids: number[];
  pagination: PaginationMeta;
  status: CategoryStatus;
  errors: CategoryErrors;
  isLoadingCategory: boolean;
}

export const initialState: CategoryState = {
  categories: [],
  ids: [],
  isLoadingCategory: false,
  pagination: {
    current_page: 1,
    from: 0,
    last_page: 0,
    links: [],
    path: "",
    per_page: 0,
    to: 0,
    total: 0,
  },
  status: {
    fetch: "idle",
    manange: "idle",
    delete: "idle",
  },
  errors: {
    fetch: null,
    manange: null,
    delete: null,
  },
};

const categoriesSlice = createSlice({
  name: "category",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // get Categories
    builder.addCase(getCategories.pending, (state) => {
      state.status.fetch = "loading";
      state.errors.fetch = null;
      state.isLoadingCategory = true;
    });
    builder.addCase(
      getCategories.fulfilled,
      (
        state,
        action: PayloadAction<ApiResponse<PaginatedPayload<CategoryData>>>
      ) => {
        console.log(action);
        state.status.fetch = "succeeded";
        state.categories = action.payload.payload.data;
        state.ids = action.payload.payload.data.map((invoice) => invoice.id);
        state.isLoadingCategory = false;
        state.pagination = action.payload.payload.meta;
      }
    );
    builder.addCase(getCategories.rejected, (state, action) => {
      state.status.fetch = "failed";
      state.errors.fetch = action.payload || null;
      state.isLoadingCategory = false;
    });

    builder.addCase(manageCategory.pending, (state) => {
      state.status.manange = "loading";
      state.errors.manange = null;
    });
    builder.addCase(manageCategory.fulfilled, (state, action) => {
      state.status.manange = "succeeded";
      const updatedCategory = action.payload.payload;
      const index = state.categories.findIndex(
        (cat) => cat.id === updatedCategory.id
      );
      if (index !== -1) {
        // EDIT category
        state.categories[index] = updatedCategory;
      } else {
        // ADD new category
        state.categories.unshift(updatedCategory);
        state.ids.unshift(updatedCategory.id);
        if (state.categories.length > state.pagination.per_page) {
          state.pagination.to--;
          state.categories.pop();
        }
        state.pagination.to++;
        state.pagination.total++;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page
        );
      }
    });
    builder.addCase(manageCategory.rejected, (state, action) => {
      state.status.manange = "failed";
      state.errors.manange = action.payload || null;
    });

    // delete Category
    builder.addCase(deleteCategory.pending, (state) => {
      state.status.delete = "loading";
      state.errors.delete = null;
    });
    builder.addCase(
      deleteCategory.fulfilled,
      (state, action: PayloadAction<ApiResponse<number>>) => {
        state.status.delete = "succeeded";
        const deletedCategoryId = action.payload.payload;
        state.categories = state.categories.filter(
          (category) => category.id !== deletedCategoryId
        );
        state.ids = state.ids.filter((id) => id !== deletedCategoryId);
        state.pagination.to--;
        state.pagination.total--;
        state.pagination.last_page = Math.ceil(
          state.pagination.total / state.pagination.per_page
        );
      }
    );
    builder.addCase(deleteCategory.rejected, (state, action) => {
      state.status.delete = "failed";
      state.errors.delete = action.payload || null;
    });
  },
});

export default categoriesSlice.reducer;

export const selectCategoriesState = (state: any) =>
  state.category as typeof initialState;
